import React from 'react';
import { Link, useStaticQuery, graphql } from 'gatsby';
import { styled } from '@mui/material';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import { RichText } from '@graphcms/rich-text-react-renderer';
import key from 'weak-key';
import theme from '../../theme';
import GlobalGrid from '../GlobalGrid';
import SvgHpPopOs from '../icon/footer-hp-pop-os';
import FacebookWhite from '../icon/facebook-white';
import LinkedInWhite from '../icon/linkedin-white';
import TwitterWhite from '../icon/twitter-white';
import YoutubeWhite from '../icon/youtube-white';
import GithubWhite from '../icon/github-white';
import InstagramWhite from '../icon/instagram-white';
import TelegramWhite from '../icon/telegram-white';
import { RichTextElementType } from '../../settings';
import { useGlobalState, User, LinkObj, Product } from '../GlobalState';

type GlobalFooterProps = {
  hideFooterLinks: boolean;
};

const CustomFooter = styled('footer')`
  box-sizing: border-box;
  background-color: ${theme.palette.greyscale.black};
  @media (max-width: ${theme.breakpoints.values.lg}px) {
    padding: 40px 0 32px;
  }
  padding: 40px 0 32px;
`;

const StyledGrid = styled(Grid)`
  &.MuiGrid-item {
    padding-top: 0px;
    @media (max-width: ${theme.breakpoints.values.sm}px) {
      padding-top: 24px;
    }
  }
`;

const Icons: React.FC<{ icon: string }> = ({ icon }) =>
  ({
    Twitter: <TwitterWhite />,
    Facebook: <FacebookWhite />,
    LinkedIn: <LinkedInWhite />,
    YouTube: <YoutubeWhite />,
    GitHub: <GithubWhite />,
    Instagram: <InstagramWhite />,
    Telegram: <TelegramWhite />,
  }[icon] ?? null);

const ExternalLink = styled('a')`
  color: ${theme.palette.greyscale.white};
  font-family: ${theme.typography.bodyS.fontFamily};
  font-size: ${theme.typography.bodyS.fontSize};
  font-weight: ${theme.typography.bodyS.fontWeight};
  line-height: ${theme.typography.bodyS.lineHeight};
  margin-right: 26px;
  &:hover {
    color: ${theme.palette.cyan};
    text-decoration: underline;
  }
  &:active {
    color: ${theme.palette.cyan};
  }
  @media (max-width: ${theme.breakpoints.values.sm}px) {
    margin-right: 0px;
    margin-bottom: 26px;
  }
`;

const FooterLink = styled(Link)`
  color: ${theme.palette.greyscale.white};
  font-family: 'HCo Gotham SSm';
  font-size: 18px;
  line-height: 28px;
  &:hover {
    color: ${theme.palette.cyan};
    text-decoration: underline;
  }
  &:active {
    color: ${theme.palette.cyan};
  }
  @media (max-width: ${theme.breakpoints.values.md}px) {
    flex: 1 0 33%;
    margin-left: 0px;
    &.last-link {
      flex: 1 0 100%;
      padding-top: 24px;
      margin-left: 0px;
    }
    :not(style) + :not(style) {
      margin-left: 0px;
    }
    @media (max-width: ${theme.breakpoints.values.sm}px) {
      &.last-link {
        padding-top: 0px;
      }
    }
  }
`;

const CustomHeading = styled('div')`
  font-family: 'Roboto Mono';
  font-size: 18px;
  line-height: 28px;
  letter-spacing: 2px;
  font-weight: 500;
  text-transform: uppercase;
`;

const StyledIconButton = styled(IconButton)`
  padding-left: 0px;
`;

/**
 * Splits uni-dimensional array of footer links into an array
 * of two arrays to be used as Stacks.
 * @param linksArray Footer links in a uni-dimensional array.
 * @param user User Object
 * @param purchases Array of purchases
 * @returns 2-length Array of arrays of link objects.
 */
const chunkLinks = (
  linksArray: Array<LinkObj>,
  user: User,
  purchases: Array<Product>,
) => {
  if (!linksArray || !linksArray.length) {
    return [];
  }

  const filteredArray = linksArray.filter((link: LinkObj) => {
    if (link.userOnly && !user) {
      return false;
    }
    if (link.purchasedOnly && !purchases.length) {
      return false;
    }
    return true;
  });

  return [
    filteredArray.slice(0, Math.ceil(filteredArray.length / 2)),
    filteredArray.slice(Math.ceil(filteredArray.length / 2)),
  ];
};

const GlobalFooter: React.FC<GlobalFooterProps> = ({
  hideFooterLinks = false,
}) => {
  const [{ user, purchased }] = useGlobalState();

  const { linkData, footerContent } =
    useStaticQuery<GatsbyTypes.FooterContentQuery>(graphql`
      query FooterContent {
        linkData: graphCmsNavbar05(name: { eq: "footer" }) {
          links {
            url
            name
            id
            userOnly
            purchasedOnly
          }
        }
        footerContent: graphCmsFooterContent05(title: { eq: "footer-info" }) {
          copyright
          hpSocialLinksTitle
          popOsSocialLinksTitle
          socialLinkshp05 {
            name
            url
            icon
          }
          socialLinksPop05 {
            name
            url
            icon
          }
          content {
            raw
          }
          externalLinks05 {
            name
            url
            externalPdf {
              url
            }
          }
        }
      }
    `);

  let footerLinks = linkData?.links;
  footerLinks = footerLinks?.filter(
    (item) => item.name !== 'HP Mouse' && item.name !== 'System76 Keyboard',
  );

  const chunkedLinks = chunkLinks(footerLinks, user, purchased);

  return (
    <CustomFooter>
      <GlobalGrid>
        <StyledGrid
          item
          xs={12}
          sx={{
            '&.MuiGrid-item': {
              paddingTop: theme.spacing(5),
              paddingBottom: theme.spacing(4),
              paddingLeft: theme.spacing(0),
              [theme.breakpoints.down('sm')]: {
                paddingBottom: theme.spacing(1),
              },
            },
          }}
        >
          <SvgHpPopOs
            sx={{
              fontSize: 177,
              height: '96px',
              width: '177px',
              [theme.breakpoints.down('md')]: {
                marginLeft: '-10px',
              },
            }}
          />
        </StyledGrid>
        {!hideFooterLinks &&
          chunkedLinks?.map((chunk: Array<LinkObj>) => (
            <StyledGrid
              item
              key={key(chunk)}
              xs={12}
              sm
              className="footerLinks"
            >
              <Stack spacing={3} className="footerLinkColumn">
                {chunk?.map((link: LinkObj) => (
                  <FooterLink
                    className="footerLink"
                    key={key(link)}
                    to={link.url}
                  >
                    {link.name}
                  </FooterLink>
                ))}
              </Stack>
            </StyledGrid>
          ))}
        <StyledGrid container item xs={12} lg={5} columnGap={7}>
          <StyledGrid
            item
            sx={{
              [theme.breakpoints.down('lg')]: {
                '&.MuiGrid-item': {
                  paddingTop: theme.spacing(5),
                  [theme.breakpoints.down('sm')]: {
                    paddingTop: theme.spacing(2),
                  },
                },
              },
            }}
          >
            <CustomHeading>{footerContent?.hpSocialLinksTitle}</CustomHeading>
            <Stack spacing={1} direction="row">
              {footerContent?.socialLinkshp05?.map((link: any) => (
                <a
                  href={link.url}
                  key={link.name}
                  target="_blank"
                  rel="noreferrer"
                >
                  <StyledIconButton aria-label={link.name} disableRipple>
                    <Icons icon={link.icon} />
                  </StyledIconButton>
                </a>
              ))}
            </Stack>
          </StyledGrid>
          <StyledGrid
            item
            sx={{
              [theme.breakpoints.down('lg')]: {
                '&.MuiGrid-item': { paddingTop: theme.spacing(5) },
              },
              [theme.breakpoints.down('sm')]: {
                '&.MuiGrid-item': { paddingTop: theme.spacing(2) },
              },
            }}
          >
            <CustomHeading>
              {footerContent?.popOsSocialLinksTitle}
            </CustomHeading>
            <Stack spacing={1} direction="row">
              {footerContent?.socialLinksPop05?.map((link: any) => (
                <a
                  href={link.url}
                  key={link.name}
                  target="_blank"
                  rel="noreferrer"
                >
                  <StyledIconButton aria-label={link.name} disableRipple>
                    <Icons icon={link.icon} />
                  </StyledIconButton>
                </a>
              ))}
            </Stack>
          </StyledGrid>
        </StyledGrid>
        <StyledGrid
          item
          xs={12}
          sx={{ '&.MuiGrid-item': { padding: `${theme.spacing(6)} 1rem` } }}
        >
          <Divider
            variant="middle"
            sx={{ width: '100%', borderBottomWidth: '3px' }}
          />
        </StyledGrid>
        <StyledGrid item xs={12}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            sx={{
              marginBottom: `${theme.spacing(6)}`,
              flexWrap: 'wrap',
              minHeight: `${theme.spacing(11)}`,
              [theme.breakpoints.down('md')]: {
                minHeight: '132px',
              },
              [theme.breakpoints.down('sm')]: {
                minHeight: '572px',
                marginBottom: '0px',
              },
            }}
          >
            {footerContent?.externalLinks05?.map((link: any) => (
              <ExternalLink
                href={link.url || link.externalPdf?.url}
                key={key(link)}
                target="_blank"
                rel="noreferrer"
              >
                {link.name}
              </ExternalLink>
            ))}
          </Stack>
        </StyledGrid>
        <StyledGrid item xs={12}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            {footerContent?.copyright?.split('\n').map((item: string) => (
              <Typography variant="caption" key={item}>
                {item}
              </Typography>
            ))}
          </Stack>
        </StyledGrid>
        <StyledGrid
          item
          xs={12}
          sx={{ '&.MuiGrid-item': { paddingTop: theme.spacing(1) } }}
        >
          {footerContent?.content?.raw?.children?.map((child: any) => {
            if (child.type === RichTextElementType.PARAGRAPH) {
              return (
                <p key={key(child)}>
                  <Typography variant="caption">
                    <RichText content={child.children} />
                  </Typography>
                </p>
              );
            }
            return <RichText key={key(child)} content={child} />;
          })}
        </StyledGrid>
      </GlobalGrid>
    </CustomFooter>
  );
};

export default GlobalFooter;
